import { setLoginAidtCookie } from '@ui/mutations/aidtLogin/setLoginAidtCookie';
import { print } from 'graphql/language/printer';
import { clearCookies } from '@ui/components/recipes/AuthAidt/clearCookies';
import { useLingui } from '@lingui/react';
import { isAIDTUser } from '@ui/components/aidt/utils/isAIDTUser';
import { AidtLoginDocument } from '@ui/mutations/aidtLogin/aidtLogin.generated';
import {
  ApolloClient,
  ApolloClientOptions,
  ApolloLink,
  from,
  fromPromise,
  HttpLink,
  NormalizedCacheObject,
  split,
} from '@apollo/client';
import { CachePersistor, PersistentStorage } from 'apollo3-cache-persist';
import { DateTime } from 'luxon';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { omitDeep } from '@shared/utils/omitdeep';
import { del, get, set } from 'idb-keyval';
import { onError } from '@apollo/client/link/error';
import Cookies from 'js-cookie';
import { setContext } from '@apollo/client/link/context';
import createCache from '@shared/stores/apollo/createCache';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { Client, ClientOptions, createClient } from 'graphql-ws';
import { addAction, addError, setGlobalContextProperty } from '@ui/utils/apm/apm';
import { createPersistLink } from '@shared/stores/apollo/persistLink';
import { createApqLink } from '@shared/stores/apollo/persistedQueryLink';
import { ApolloPersistOptions } from 'apollo3-cache-persist/lib/types';
import merge from 'deepmerge';
import { isEqual, isNil, omitBy } from 'lodash-es';
import { useMemo } from 'react';
import { isQRLogin } from '@shared/utils/auth';
import { isProject } from '@shared/constants/projectTarget';
import { refreshAccessToken, handleRefreshFailure } from '@shared/utils/tokenRefreshManager';
import { AIDT_ACCESS_ID, AIDT_ACCESS_TOKEN, AIDT_LOGIN_AUTH_PARTNER_ID } from '@shared/constants/token';
import { kerisPersonalDataLink } from './kerisPersonalDataLink';
import { showAlert } from '@ui/components/patterns/ConcurrentAuthAlert';
import { requestDurationLink } from './requestDurationLink';
import {
  WebSocketConnectionState,
  wsConnectionManager,
  MAX_RETRY_BEFORE_FALLBACK,
} from './WebSocketConnectionManager';

export type { WebSocketConnectionState };
export {
  wsConnectionManager,
  getWebSocketConnectionState,
  subscribeToWebSocketState,
} from './WebSocketConnectionManager';

export const LOGIN_AUTH_TOKEN_KEY = 'mdLoginToken';
export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

export const cache = createCache();

type Input = {
  option: Omit<ApolloClientOptions<any>, 'cache' | 'connectToDevTools' | 'link' | 'ssrMode' | 'uri'> & {
    uri: string;
  };
  wsURL?: string;
  links?: ApolloLink[];
};

export class LocalForageWrapper implements PersistentStorage<string | object | null> {
  protected storage;

  constructor(storage: LocalForageInterface) {
    this.storage = storage;
  }

  getItem(key: string): Promise<string | null> {
    return this.storage.getItem(key);
  }

  removeItem(key: string): Promise<void> {
    return this.storage.removeItem(key);
  }

  setItem(key: string, value: string | object | null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.storage
        .setItem(key, value)
        .then(() => resolve())
        .catch((e) => reject(e));
    });
  }
}

interface LocalForageInterface {
  // Actual type definition: https://github.com/localForage/localForage/blob/master/typings/localforage.d.ts#L17
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string | object | null): Promise<string | object | null>;
  removeItem(key: string): Promise<void>;
}

function findRefEntitis(item: any): Array<string | null> | null {
  if (Array.isArray(item)) {
    return item.map((i) => findRefEntitis(i)).flat();
  }

  if (item?.__ref) {
    return [item.__ref];
  }

  if (typeof item === 'object' && item !== null) {
    return Object.keys(item)
      .map((key) => findRefEntitis(item[key]))
      .flat();
  }

  return null;
}

function getAIDTHeaders() {
  return omitBy(
    {
      'Aidt-Access-Id': Cookies.get(AIDT_ACCESS_ID),
      'Aidt-Token': Cookies.get(AIDT_ACCESS_TOKEN),
      'Partner-Id': Cookies.get(AIDT_LOGIN_AUTH_PARTNER_ID),
    },
    isNil,
  );
}

let persistor: CachePersistor<any> | undefined;

export async function getPersistor<T>(options?: Partial<ApolloPersistOptions<T>>) {
  if (typeof window === 'undefined') {
    return;
  }

  if (persistor) {
    return persistor;
  }

  // https://github.com/apollographql/apollo-cache-persist/tree/master/examples/react-native/src/utils/persistence
  persistor = new CachePersistor({
    trigger: 'write',
    storage: new LocalForageWrapper({
      getItem: async (key: string) => (await get(key)) ?? null,
      setItem: async (key: string, value: string | object | null) => {
        await set(key, value);
        return value;
      },
      removeItem: (key: string) => del(key),
    }),
    persistenceMapper: async (data: any) => {
      const parsed = JSON.parse(data);

      const mapped: any = {};
      const rootPersistEntities: any[] = [];
      const rootQuery = parsed['ROOT_QUERY'];

      mapped['ROOT_QUERY'] = Object.keys(rootQuery).reduce(
        (obj: any, key: string) => {
          if (key === '__typename') return obj;

          if (/@persist$/.test(key)) {
            obj[key] = rootQuery[key];

            const entities = (findRefEntitis(rootQuery[key]) || []).filter((item) => !!item);
            rootPersistEntities.push(...entities);
          }

          return obj;
        },
        { __typename: 'Query' },
      );

      const nestedPersistEntities: string[] = [];

      const searchPersistEntities = [...rootPersistEntities];

      while (searchPersistEntities.length > 0) {
        const entity = searchPersistEntities.pop();

        const entities = (findRefEntitis(parsed[entity]) || []).filter((item) => !!item);
        rootPersistEntities.push(...entities);
        searchPersistEntities.push(...entities);
      }

      [...rootPersistEntities, ...nestedPersistEntities].reduce((obj, key) => {
        obj[key] = parsed[key];
        return obj;
      }, mapped);

      return mapped;
    },
    ...options,
    cache,
  });

  return persistor;
}

interface RestartableClient extends Client {
  restart(): void;
  terminate(): void;
}

// Ping/Pong 타임아웃 설정
const PONG_TIMEOUT_MS = 5000; // pong 응답 대기 시간: 5초

function createRestartableClient(options: ClientOptions): RestartableClient {
  let restartRequested = false;
  let restart = () => {
    restartRequested = true;
  };

  // Pong 타임아웃 관리
  let activeSocket: WebSocket | null = null;
  let pongTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const clearPongTimeout = () => {
    if (pongTimeoutId) {
      clearTimeout(pongTimeoutId);
      pongTimeoutId = null;
    }
  };

  const client = createClient({
    ...options,
    on: {
      ...options.on,
      opened: (socket: unknown) => {
        options.on?.opened?.(socket);
        const ws = socket as WebSocket;
        activeSocket = ws;

        restart = () => {
          if (ws.readyState === WebSocket.OPEN) {
            // if the socket is still open for the restart, do the restart
            if (ws.close) {
              ws.close(4205, 'Client Restart');
            }
          } else {
            // otherwise the socket might've closed, indicate that you want
            // a restart on the next opened event
            restartRequested = true;
          }
        };

        // just in case you were eager to restart
        if (restartRequested) {
          restartRequested = false;
          restart();
        }
      },
      // Ping 전송 시 pong 타임아웃 설정
      ping: (received, payload) => {
        options.on?.ping?.(received, payload);

        if (!received) {
          // ping을 보낸 경우 - pong 타임아웃 설정
          clearPongTimeout();
          pongTimeoutId = setTimeout(() => {
            addAction('ws-strategy WebSocket pong timeout - terminating', {
              message: `No pong received within ${PONG_TIMEOUT_MS}ms`,
              timestamp: Date.now(),
            });

            // pong 타임아웃 시 terminate로 완전 종료 후 재연결
            // close()만으로는 잔여 소켓이 있을 수 있음
            if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
              activeSocket.close(4408, 'Pong Timeout');
            }
            // terminate 후 graphql-ws가 자동으로 재연결 시도
            client.terminate();
          }, PONG_TIMEOUT_MS);
        }
      },
      // Pong 수신 시 타임아웃 클리어
      pong: (received, payload) => {
        options.on?.pong?.(received, payload);

        if (received) {
          clearPongTimeout();
        }
      },
      closed: (event) => {
        options.on?.closed?.(event);
        clearPongTimeout();
        activeSocket = null;
      },
    },
  });

  return {
    ...client,
    restart: () => restart(),
    terminate: () => client.terminate(),
  };
}

/**
 * WebSocket 연결을 재시작합니다.
 * 토큰 갱신 후 호출하여 새로운 인증 정보로 재연결할 수 있습니다.
 */
export function restartWebSocketConnection() {
  if (wsClient) {
    addAction('ws-strategy restartWebSocketConnection called', {
      timestamp: Date.now(),
    });
    wsClient.restart();
  } else {
    addAction('ws-strategy restartWebSocketConnection failed', {
      reason: 'wsClient is null',
      timestamp: Date.now(),
    });
  }
}

/**
 * WebSocket 연결을 완전히 종료합니다.
 * 기존 클라이언트 인스턴스를 폐기하고 재연결을 시도하지 않습니다.
 */
export function terminateWebSocketConnection() {
  if (wsClient) {
    addAction('ws-strategy terminateWebSocketConnection called', {
      timestamp: Date.now(),
    });
    wsClient.terminate();
  } else {
    addAction('ws-strategy terminateWebSocketConnection failed', {
      reason: 'wsClient is null',
      timestamp: Date.now(),
    });
  }
}

export class TokenManager {
  private static instance: TokenManager;
  private lastToken: string | undefined;

  private constructor() {
    this.lastToken = undefined;
  }

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  getLastToken(): string | undefined {
    return this.lastToken;
  }

  setLastToken(token: string | undefined): void {
    this.lastToken = token;
  }

  clearToken(): void {
    this.lastToken = undefined;
  }
}
export function createApolloClient(input: Input) {
  const { links = [], wsURL, option } = input;
  const ssrMode = typeof window === 'undefined';

  const authLink = setContext(async (operation, { headers }) => {
    const tokenManager = TokenManager.getInstance();
    const lastToken = tokenManager.getLastToken();

    let token = Cookies.get(LOGIN_AUTH_TOKEN_KEY) ?? lastToken;

    if (token !== lastToken) {
      tokenManager.setLastToken(token);
    }

    setGlobalContextProperty('accessToken', token);

    if (token && !isQRLogin()) {
      const decoded = jwtDecode<JwtPayload>(token);

      if (decoded.exp) {
        if (isProject('mildang')) {
          // 선제적 토큰 갱신: 만료 1분 전에 /auth/refresh를 호출하여
          // 요청 실패 없이 새 Access Token으로 교체한다.
          const now = Date.now() / 1000;
          const bufferSeconds = 60;

          if (decoded.exp - now < bufferSeconds) {
            const result = await refreshAccessToken();
            if (result.success) {
              token = result.token;
            } else if (result.reason === 'auth') {
              handleRefreshFailure();
              return { headers };
            }
            // transient 실패: 기존 토큰으로 요청 시도 (다음 요청 시 재시도 기회)
          }
        } else if (isProject('hub')) {
        } else {
          const isExpireIn1Minute =
            DateTime.fromMillis(decoded.exp * 1000) <= DateTime.local().plus({ minutes: 1 });

          let needClear = false;
          if (isExpireIn1Minute) {
            if (isAIDTUser()) {
              const accessId = Cookies.get(AIDT_ACCESS_ID);
              const accessToken = Cookies.get(AIDT_ACCESS_TOKEN);

              if (!accessId || !accessToken) {
                needClear = true;
              } else {
                if (typeof option.uri === 'string') {
                  const response = await fetch(option.uri, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      ...(option?.headers || {}),
                      ...getAIDTHeaders(),
                    },
                    body: JSON.stringify({
                      query: print(AidtLoginDocument),
                      variables: {
                        input: {
                          access_id: accessId,
                          token: accessToken,
                        },
                      },
                    }),
                  });

                  const { data } = await response.json();

                  if (data?.loginAidt) {
                    setLoginAidtCookie(data.loginAidt);
                    token = data.loginAidt.token;
                  } else {
                    needClear = true;
                  }
                }
              }
            } else {
              // AIDT가 아닌 유저: 선제 refresh 시도
              const result = await refreshAccessToken();
              if (result.success) {
                token = result.token;
              } else if (result.reason === 'auth') {
                needClear = true;
              }
              // transient 실패: 기존 토큰으로 요청 시도
            }

            if (needClear) {
              token = undefined;
              if (isAIDTUser()) {
                clearCookies();
              } else {
                handleRefreshFailure();
                return { headers };
              }
            }
          }
        }
      }
    }

    const locale = getLocale();

    // 개발 환경에서 x-ihfb-pr 쿠키가 있으면 헤더에 추가
    const prNumber = process.env.NEXT_PUBLIC_APP_ENV === 'development' ? Cookies.get('x-ihfb-pr') : undefined;

    return {
      headers: {
        ...(locale ? { 'accept-language': locale } : {}),
        ...headers,
        ...(option?.headers || {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(isProject('schoolpt') ? getAIDTHeaders() : {}),
        ...(prNumber ? { 'x-ihfb-pr': prNumber } : {}),
      },
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    const context = {
      'apollo.client.variablesStr': JSON.stringify(omitDeep(operation.variables, ['password'])),
      'apollo.client.operationName': operation.operationName,
    };
    if (operation.operationName === 'VerifyAuthority') {
      return;
    }
    if (graphQLErrors) {
      const debugToken = Cookies.get(LOGIN_AUTH_TOKEN_KEY);

      if (
        graphQLErrors.some(
          (e) => process.env.NEXT_PUBLIC_AIDT_PORTAL_HOST && e.extensions?.code === 'DUPLICATE_LOGIN',
        )
      ) {
        showAlert();
        clearCookies();
        return;
      }

      // 서버 401 fallback: authLink의 선제 refresh가 실패하거나 서버 시간 차이 등으로
      // UNAUTHENTICATED가 발생할 경우, 토큰 갱신 후 실패한 요청을 재시도한다.
      // 단, Access Token 자체가 없으면 "미로그인 상태"이므로 refresh를 시도하지 않는다.
      if (graphQLErrors.some((e) => e.extensions?.code === 'UNAUTHENTICATED')) {
        if (!Cookies.get(LOGIN_AUTH_TOKEN_KEY)) return;

        return fromPromise(
          refreshAccessToken().then((result) => {
            if (result.success) {
              const oldHeaders = operation.getContext().headers;
              operation.setContext({
                headers: { ...oldHeaders, authorization: `Bearer ${result.token}` },
              });
            } else if (result.reason === 'auth') {
              handleRefreshFailure();
            }
            // transient 실패: forward(operation)로 원래 요청이 재시도되지만 토큰이 없어 실패할 수 있음
            // → 유저에게 에러 UI가 표시되며, 다음 요청 시 refresh 재시도 기회가 주어짐
          }),
        ).flatMap(() => forward(operation));
      }

      graphQLErrors.forEach((graphQLError) => {
        addError(new Error(`GraphQLError:${graphQLError.message}`), {
          ...context,
          'error.type': 'GraphQLError',
          'error.message': graphQLError.message,
          // 'error.stack': graphQLError.stack,
          // 'error.source': graphQLError.source,
          'apollo.client.error.extensions': graphQLError.extensions,
          'apollo.client.token': debugToken,
        });
      });
    }
    if (networkError) {
      addError(networkError, context);
    }
  });

  const persistLink = createPersistLink();

  const httpLink = from([
    ...links,
    persistLink,
    authLink,
    errorLink,
    requestDurationLink,
    kerisPersonalDataLink,
    ...createApqLink(),
    new HttpLink({ uri: option.uri, credentials: 'include' }),
  ]);

  let link: ApolloLink;

  // WebSocket 인증 재시도 상태 관리
  let lastCloseWasAuthError = false;
  let wsAuthRetryCount = 0;
  const MAX_WS_AUTH_RETRIES = 1; // 토큰 갱신 후 1회만 재연결 시도

  if (ssrMode || !wsURL) {
    link = httpLink;
  } else {
    link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      new GraphQLWsLink(
        (wsClient = createRestartableClient({
          url: wsURL,
          connectionParams: async () => {
            // 매 연결 시도마다 최신 토큰 사용
            const token = option?.headers?.authorization || Cookies.get(LOGIN_AUTH_TOKEN_KEY);
            return { authorization: token ? `Bearer ${token}` : undefined };
          },
          keepAlive: 1000 * 10, // 10초마다 ping
          connectionAckWaitTimeout: 3000, // 3초 - ConnectionAck 대기 시간 (네트워크 지연 고려)
          retryAttempts: Infinity, // 무한 재시도 (polling fallback 전까지)
          retryWait: async (retries) => {
            // 인증 실패(4403)로 인한 재시도인 경우: 토큰 갱신 후 즉시 재연결 (백오프 스킵)
            if (lastCloseWasAuthError) {
              lastCloseWasAuthError = false;

              addAction('ws-strategy WebSocket auth retry - refreshing token', {
                retries,
                wsAuthRetryCount,
                timestamp: Date.now(),
              });

              wsConnectionManager.setState('reconnecting', retries);

              const result = await refreshAccessToken();
              if (result.success) {
                addAction('ws-strategy WebSocket token refreshed - retrying connection', {
                  timestamp: Date.now(),
                });
                return; // 지연 없이 즉시 재연결 → connectionParams가 새 토큰 사용
              }

              if (result.reason === 'auth') {
                addAction('ws-strategy WebSocket token refresh auth failed - handleRefreshFailure', {
                  timestamp: Date.now(),
                });
                handleRefreshFailure();
                return;
              }

              // transient 실패: 로그아웃하지 않고, 일반 백오프 재시도로 전환
              addAction('ws-strategy WebSocket token refresh transient failed - will retry with backoff', {
                timestamp: Date.now(),
              });
              wsAuthRetryCount = 0; // 다음 4403에서 다시 refresh 시도 가능하도록 초기화
            }

            // 일반 재시도: 지수 백오프 + jitter (최대 30초)
            const baseDelay = Math.min(1000 * Math.pow(2, retries), 30000);
            const jitter = Math.random() * 1000; // 0~1초 랜덤
            const delay = baseDelay + jitter;

            addAction('ws-strategy WebSocket retryWait', {
              retries,
              delay,
              timestamp: Date.now(),
            });

            // 연결 상태 업데이트
            wsConnectionManager.setState('reconnecting', retries);

            // MAX_RETRY_BEFORE_FALLBACK 초과 시 failed 상태로 전환
            if (retries >= MAX_RETRY_BEFORE_FALLBACK) {
              wsConnectionManager.setState('failed', retries);
              addAction('ws-strategy WebSocket max retries reached - fallback to polling', {
                retries,
                maxRetries: MAX_RETRY_BEFORE_FALLBACK,
                timestamp: Date.now(),
              });
            }

            await new Promise((resolve) => setTimeout(resolve, delay));
          },
          shouldRetry: (errOrCloseEvent) => {
            if (errOrCloseEvent instanceof CloseEvent) {
              // 인증 에러(4403: Forbidden) — 토큰 갱신 후 재시도 가능
              if (errOrCloseEvent.code === 4403) {
                // Access Token이 없거나 이미 갱신 재시도를 소진한 경우 중단
                if (!Cookies.get(LOGIN_AUTH_TOKEN_KEY) || wsAuthRetryCount >= MAX_WS_AUTH_RETRIES) {
                  addAction('ws-strategy WebSocket auth error - no retry', {
                    code: errOrCloseEvent.code,
                    reason: errOrCloseEvent.reason,
                    wsAuthRetryCount,
                  });
                  wsConnectionManager.setState('failed');
                  return false;
                }

                // 토큰 갱신 후 재시도를 위해 true 반환 (retryWait에서 refresh 수행)
                lastCloseWasAuthError = true;
                wsAuthRetryCount++;
                addAction('ws-strategy WebSocket auth error - will attempt token refresh', {
                  code: errOrCloseEvent.code,
                  reason: errOrCloseEvent.reason,
                  wsAuthRetryCount,
                  maxWsAuthRetries: MAX_WS_AUTH_RETRIES,
                });
                return true;
              }
              // 다른 모든 CloseEvent는 재시도
              // - 1006: 비정상 종료 (네트워크 끊김)
              // - 4408: Pong 타임아웃
              // - 4418: ConnectionAck 타임아웃
              // 참고: 4400, 4401, 4500 등 치명적 코드는 graphql-ws가 내부적으로 재시도 안 함
              return true;
            }
            // CloseEvent가 아닌 에러도 재시도 (네트워크 에러 등)
            return true;
          },
          on: {
            connecting: () => {
              const currentState = wsConnectionManager.getState();
              // 최초 연결이 아닌 경우에만 reconnecting으로 설정
              if (currentState !== 'closed' && currentState !== 'connecting') {
                wsConnectionManager.setState('reconnecting');
              } else {
                wsConnectionManager.setState('connecting');
              }

              addAction('ws-strategy createApolloClient connecting', {
                timestamp: Date.now(),
                previousState: currentState,
              });
            },
            connected: (socket: unknown) => {
              // 연결 성공 - 상태 및 재시도 카운트 초기화
              wsConnectionManager.setState('connected');
              wsConnectionManager.resetRetryCount();
              wsAuthRetryCount = 0;

              const ws = socket as WebSocket;
              addAction('ws-strategy createApolloClient connected', {
                timestamp: Date.now(),
                readyState: ws.readyState,
              });
            },
            closed: (event) => {
              const closeEventData =
                event instanceof CloseEvent
                  ? {
                      code: event.code,
                      reason: event.reason,
                      wasClean: event.wasClean,
                    }
                  : event;

              // 정상 종료(1000)가 아닌 경우 reconnecting 상태로
              if (event instanceof CloseEvent && event.code !== 1000) {
                wsConnectionManager.setState('reconnecting');
              } else {
                wsConnectionManager.setState('closed');
              }

              // 4418: Connection acknowledgement timeout
              if (event instanceof CloseEvent && event.code === 4418) {
                addAction('ws-strategy WebSocket ConnectionAck timeout', {
                  event: closeEventData,
                  message: 'Server did not respond with ConnectionAck within timeout',
                });
              } else if (event instanceof CloseEvent && event.code === 4408) {
                addAction('ws-strategy WebSocket Pong timeout close', {
                  event: closeEventData,
                  message: 'Connection closed due to pong timeout',
                });
              } else {
                addAction('ws-strategy createApolloClient createClient onClosed', {
                  event: closeEventData,
                });
              }
            },
            error: (error) => {
              // WebSocket onerror는 기본 Event 객체만 제공 (구체적 정보 없음)
              // 실제 에러 정보는 onClosed의 CloseEvent에서 확인 가능
              addAction('ws-strategy createApolloClient createClient onError', {
                error:
                  error instanceof Event
                    ? {
                        type: error.type,
                        isTrusted: error.isTrusted,
                        timestamp: Date.now(),
                      }
                    : error,
              });
            },
          },
        })),
      ),
      // TODO: retry certain operation with function params(attempts, delay)
      // new RetryLink({ attempts: { max: 3 }, delay }),
      httpLink,
    );
  }

  const apolloClientOption = { ...option } as ApolloClientOptions<any> & {
    connectToDevTools?: boolean;
  };

  // Apollo Client 3.14+ deprecates client-level `uri` and `connectToDevTools`.
  delete apolloClientOption.uri;
  delete apolloClientOption.connectToDevTools;

  const client = new ApolloClient({
    cache,
    ssrMode,
    link: link,
    devtools: {
      enabled: true,
    },
    ...apolloClientOption,
    dataMasking: true,
  });

  return client;
}

let apolloClient: ReturnType<typeof createApolloClient>;
let wsClient: RestartableClient | null = null;

export interface initializeApolloInput extends Input {
  initialState?: NormalizedCacheObject | null;
}

export function initializeApollo(input: initializeApolloInput) {
  const { initialState, ...others } = input;

  const _apolloClient = apolloClient ?? createApolloClient(others);

  if (initialState) {
    const existingCache = _apolloClient.extract();

    const data = merge(existingCache, initialState, {
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
      ],
    });

    _apolloClient.cache.restore(data);
  }

  if (typeof window === 'undefined') return _apolloClient;
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function getApolloClient() {
  if (!apolloClient) {
    return initializeApollo({
      wsURL: process.env.NEXT_PUBLIC_WEB_SOCKET_URL as string,
      option: {
        uri: process.env.NEXT_PUBLIC_GRAPHQL_URL as string,
      },
    });
  }
  return apolloClient;
}

export function addApolloState(client, pageProps) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

let _locale: string;

function setLocale(locale: string) {
  _locale = locale;
}

function getLocale() {
  return _locale;
}

export function useApollo(pageProps, uri: string, wsURL?: string, links?: ApolloLink[]) {
  const initialState = pageProps[APOLLO_STATE_PROP_NAME];
  const { i18n } = useLingui();
  setLocale(i18n.locale);

  const store = useMemo(
    () =>
      initializeApollo({
        option: { uri: uri },
        initialState,
        wsURL,
        links,
      }),
    [uri, initialState, wsURL, links],
  );
  return store;
}
